import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Icon from "@mdi/react";
import {
  mdiArrowRight,
  mdiClose,
  mdiAirplaneTakeoff,
  mdiBriefcaseOutline,
  mdiMapMarkerPath,
  mdiShieldCheckOutline,
  mdiCellphone,
  mdiRoadVariant,
  mdiMapMarkerRadius,
  mdiStar,
  mdiChevronDoubleRight,
  mdiCompass,
  mdiMapClock,
  mdiCarMultiple,
} from "@mdi/js";

import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getBlogCards } from "../../../store/Blog/BlogAction";
import { subscribeNewsletter } from "../../../store/SubScribe/SubScribeAction";
import { subscribeAction } from "../../../store/SubScribe/SubScribeReducer";
import { getBlogCityPriority } from "../../../utils/cityProximity";

import Loading from "../../../components/Loading";
import CategoryTabsGem from "./components/CategoryTabsGem";
import FeaturedBlogCardGem from "./components/FeaturedBlogCardGem";
import BlogListItemGem from "./components/BlogListItemGem";

/**
 * Redesigned Blogs page – Immersive Travel Theme
 * Brand colours: Teal (#21A5A0), Coral (#E05A49)
 */

function stripHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function guessCategoryKeyFromBlog(blog) {
  const text = `${(blog?.title || "").toLowerCase()} ${(blog?.short_description || "").toLowerCase()} ${stripHtml(blog?.description).toLowerCase()}`;
  if (text.includes("airport") || text.includes("flight") || text.includes("terminal")) return "Airport Transfer Tips";
  if (text.includes("corporate") || text.includes("employee") || text.includes("business")) return "Corporate Travel";
  if (text.includes("road trip") || text.includes("roadtrip") || text.includes("weekend") || text.includes("outstation"))
    return "Road Trip Advice";
  if (text.includes("safety") || text.includes("secure") || text.includes("women") || text.includes("solo"))
    return "Safety & Tips";
  return "All Topics";
}

function categoryBadgeLabelFromKey(key) {
  if (key === "Airport Transfer Tips") return "Airport Tips";
  if (key === "Corporate Travel") return "Corporate";
  if (key === "Road Trip Advice") return "Local Travel";
  if (key === "Safety & Tips") return "Safety Tips";
  return "Blog";
}

// ─── Scroll Reveal Hook ──────────────────────────────────
function useScrollReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
}

// ─── Parallax Hook ───────────────────────────────────────
function useParallax(factor = 0.3) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handle = () => setOffset(window.scrollY * factor);
    window.addEventListener("scroll", handle, { passive: true });
    return () => window.removeEventListener("scroll", handle);
  }, [factor]);
  return offset;
}

// ─── Rotating Hero Backgrounds ───────────────────────────
const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=80",    // mountain road
  "https://images.unsplash.com/photo-1488085061387-422e29b40080?w=1600&q=80",    // map on table
  "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1600&q=80",    // world travel
  "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1600&q=80",    // suitcase travel
];

const TRAVEL_STATS = [
  { icon: mdiRoadVariant, value: "500+", label: "Cities Covered" },
  { icon: mdiCarMultiple, value: "2M+", label: "Rides Completed" },
  { icon: mdiMapClock, value: "24/7", label: "Support Available" },
  { icon: mdiStar, value: "4.8", label: "Rating" },
];

export default function BlogsGem() {
  const dispatch = useDispatch();
  const [activeCategory, setActiveCategory] = useState("All Topics");
  const [query, setQuery] = useState("");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [page, setPage] = useState(1);
  const [heroImgIndex, setHeroImgIndex] = useState(0);
  const pageSize = 6;
  const parallaxOffset = useParallax(0.28);
  const [heroRef, heroVisible] = useScrollReveal(0.01);
  const [featuredRef, featuredVisible] = useScrollReveal(0.05);
  const [articlesRef, articlesVisible] = useScrollReveal(0.05);
  const [statsRef, statsVisible] = useScrollReveal(0.15);

  const { blogCards, loading } = useSelector((state) => state.blogCards);
  const { location } = useSelector((state) => state.location);
  const { isSubmit: newsletterSubmitted, error: newsletterError } = useSelector(
    (state) => state.subscribe
  );

  // Rotate hero background
  useEffect(() => {
    const t = setInterval(() => setHeroImgIndex((i) => (i + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    dispatch(getBlogCards({ blog_count: 20 }));
  }, [dispatch]);

  useEffect(() => {
    if (newsletterSubmitted) {
      setNewsletterEmail("");
      dispatch(subscribeAction.clearSubmit());
    }
    if (newsletterError) {
      dispatch(subscribeAction.clearError());
    }
  }, [dispatch, newsletterSubmitted, newsletterError]);

  const categories = useMemo(
    () => [
      { label: "All Topics", icon: mdiMapMarkerRadius },
      { label: "Airport Transfer Tips", icon: mdiAirplaneTakeoff },
      { label: "Corporate Travel", icon: mdiBriefcaseOutline },
      { label: "Road Trip Advice", icon: mdiMapMarkerPath },
      { label: "Safety & Tips", icon: mdiShieldCheckOutline },
    ],
    []
  );

  const sortedBlogs = useMemo(() => {
    const list = blogCards?.data ? [...blogCards.data] : [];
    const userCity = location?.city;
    if (!userCity) return list;
    return list.sort((a, b) => getBlogCityPriority(a, userCity) - getBlogCityPriority(b, userCity));
  }, [blogCards?.data, location?.city]);

  const filteredBlogs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (sortedBlogs || []).filter((b) => {
      const key = guessCategoryKeyFromBlog(b);
      const matchCategory = activeCategory === "All Topics" || key === activeCategory;
      const haystack = `${b?.title || ""} ${b?.short_description || ""} ${stripHtml(b?.description)}`.toLowerCase();
      const matchQuery = !q || haystack.includes(q);
      return matchCategory && matchQuery;
    });
  }, [sortedBlogs, activeCategory, query]);

  const featuredBlogs = useMemo(() => filteredBlogs.slice(0, 3), [filteredBlogs]);
  const remainingBlogs = useMemo(() => filteredBlogs.slice(3), [filteredBlogs]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(remainingBlogs.length / pageSize)), [remainingBlogs.length]);
  const pagedBlogs = useMemo(() => {
    const start = (page - 1) * pageSize;
    return remainingBlogs.slice(start, start + pageSize);
  }, [remainingBlogs, page, pageSize]);

  useEffect(() => { setPage(1); }, [activeCategory, query]);

  const clearSearch = useCallback(() => setQuery(""), []);

  return (
    <div className="blogs-gem-root" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        :root {
          --teal: #21A5A0;
          --teal-dark: #128884;
          --teal-light: #EEF7F7;
          --teal-glow: rgba(33,165,160,0.25);
          --coral: #E05A49;
          --coral-dark: #c44839;
          --coral-light: #fce8e5;
          --black: #1B1B1B;
          --black-dark: #0F0F0F;
          --grey: #74757B;
          --grey-light: #F5F5F5;
          --white: #ffffff;
        }

        .blogs-gem-root { background: #f8f9fa; min-height: 100vh; }

        /* ── Animations ── */
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.92); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(33,165,160,0.15); }
          50%      { box-shadow: 0 0 40px rgba(33,165,160,0.35); }
        }
        @keyframes compassSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes floatPin {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-10px); }
        }
        @keyframes pathDraw {
          from { stroke-dashoffset: 500; }
          to   { stroke-dashoffset: 0; }
        }
        @keyframes routeDash {
          0%   { stroke-dashoffset: 1000; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes zoomFade {
          0%   { transform: scale(1); opacity: 1; }
          40%  { transform: scale(1.08); opacity: 0.7; }
          60%  { transform: scale(1.08); opacity: 0.7; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes counterIn {
          from { opacity: 0; transform: translateY(24px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .anim-hero-1 { animation: slideUp .8s ease .1s both; }
        .anim-hero-2 { animation: slideUp .8s ease .25s both; }
        .anim-hero-3 { animation: slideUp .8s ease .4s both; }
        .anim-hero-4 { animation: slideUp .8s ease .55s both; }
        .anim-fade-in { animation: fadeIn 1s ease both; }
        .anim-scale-in { animation: scaleIn .6s cubic-bezier(.22,1,.36,1) both; }

        .reveal-visible { animation: slideUpFade .7s cubic-bezier(.22,1,.36,1) both; }
        .reveal-visible-delay-1 { animation-delay: .1s; }
        .reveal-visible-delay-2 { animation-delay: .2s; }
        .reveal-visible-delay-3 { animation-delay: .3s; }
        .reveal-visible-delay-4 { animation-delay: .4s; }

        .stat-item-enter { animation: counterIn .5s ease both; }
        .stat-item-enter:nth-child(1) { animation-delay: .05s; }
        .stat-item-enter:nth-child(2) { animation-delay: .15s; }
        .stat-item-enter:nth-child(3) { animation-delay: .25s; }
        .stat-item-enter:nth-child(4) { animation-delay: .35s; }

        .compass-ring { animation: compassSpin 60s linear infinite; }
        .float-pin    { animation: floatPin 3s ease-in-out infinite; }
        .hero-bg-zoom { animation: zoomFade 6s ease-in-out infinite; }

        /* ── Hero image crossfade ── */
        .hero-crossfade {
          position: absolute; inset: -10% 0 0 0;
          height: 120%; width: 100%;
          background-size: cover; background-position: center 30%;
          transition: opacity 1.2s ease-in-out, transform 0.3s ease-out;
          will-change: opacity, transform;
        }

        /* ── Category tabs ── */
        .cat-tab {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 14px 26px; border-radius: 100px; border: 2px solid #e0e0e0;
          background: white; color: var(--grey); font-size: 1.05rem; font-weight: 600;
          cursor: pointer; transition: all .3s cubic-bezier(.22,1,.36,1); white-space: nowrap; outline: none;
        }
        .cat-tab:hover { border-color: var(--teal); color: var(--teal); background: var(--teal-light); transform: translateY(-2px); }
        .cat-tab.active { background: var(--teal); border-color: var(--teal); color: white; box-shadow: 0 6px 20px var(--teal-glow); }
        .cat-tab.active svg path { color: white; }

        /* ── Featured Cards – Travel Brochure style ── */
        .feat-card {
          border-radius: 18px; overflow: hidden; background: white;
          border: 1px solid rgba(0,0,0,.05);
          transition: transform .5s cubic-bezier(.22,1,.36,1), box-shadow .5s ease;
          text-decoration: none; color: inherit; display: flex; flex-direction: column;
          perspective: 800px;
          position: relative;
        }
        .feat-card:hover { transform: translateY(-10px) scale(1.01); box-shadow: 0 35px 80px -18px rgba(33,165,160,.28); }
        .feat-card:hover .feat-card__img { transform: scale(1.12); }
        .feat-card:hover .feat-card__cta { color: var(--coral); gap: 12px; }
        .feat-card:hover .feat-card__cta-arrow { transform: translateX(8px) rotate(0deg); }
        .feat-card:hover .feat-card__number { opacity: 1; transform: translateY(0); }
        .feat-card:hover .feat-card__read-floating { opacity: 1; transform: translateY(0); }

        /* Image area with gradient overlay */
        .feat-card__img-wrap { overflow: hidden; height: 240px; position: relative; }
        .feat-card__img { width: 100%; height: 100%; object-fit: cover; transition: transform .7s cubic-bezier(.22,1,.36,1); }
        .feat-card__img-overlay {
          position: absolute; bottom: 0; left: 0; right: 0;
          height: 60%; 
          background: linear-gradient(0deg, rgba(0,0,0,0.35) 0%, transparent 100%);
          pointer-events: none;
        }

        /* Category badge with map pin */
        .feat-card__badge {
          position: absolute; top: 14px; left: 14px;
          background: rgba(255,255,255,0.92); backdrop-filter: blur(8px);
          color: var(--teal-dark); font-size: .75rem; font-weight: 800;
          letter-spacing: .06em; text-transform: uppercase;
          padding: 5px 14px 5px 10px; border-radius: 100px;
          display: inline-flex; align-items: center; gap: 5px;
          border: 1px solid rgba(33,165,160,0.12);
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }

        /* Floating read time badge */
        .feat-card__read-floating {
          position: absolute; top: 14px; right: 14px;
          background: rgba(255,255,255,0.92); backdrop-filter: blur(8px);
          color: #0B1F3A; font-size: .7rem; font-weight: 700;
          padding: 4px 12px 4px 10px; border-radius: 100px;
          display: inline-flex; align-items: center; gap: 4px;
          border: 1px solid rgba(0,0,0,0.04);
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          opacity: 1; transform: translateY(0);
          transition: all .4s ease;
        }

        /* Star stamp */
        .feat-card__number {
          position: absolute; bottom: 12px; right: 14px;
          width: 32px; height: 32px;
          border-radius: 50%;
          background: rgba(255,255,255,0.9); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          opacity: 0.8; transform: translateY(4px);
          transition: all .4s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .feat-card:hover .feat-card__number { opacity: 1; transform: translateY(0); }

        /* Body section */
        .feat-card__body {
          padding: 1.5rem 1.5rem 1.8rem;
          flex: 1; display: flex; flex-direction: column;
          position: relative;
        }
        .feat-card__body::before {
          content: '';
          position: absolute; top: 0; left: 1.5rem; right: 1.5rem;
          height: 2px;
          background: linear-gradient(90deg, var(--teal), var(--coral), transparent);
          border-radius: 1px;
        }
        .feat-card__meta {
          font-size: .85rem; color: var(--grey); font-weight: 500;
          margin-bottom: .7rem; display: flex; align-items: center; gap: .4rem;
          padding-top: .5rem;
        }
        .feat-card__title {
          font-family: 'Inter', sans-serif; font-size: 1.35rem; font-weight: 800;
          line-height: 1.35; color: var(--black-dark); margin: 0 0 .6rem;
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
        }
        .feat-card__excerpt {
          font-size: 1rem; color: var(--grey); line-height: 1.65;
          margin: 0 0 1rem; flex: 1;
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
        }
        .feat-card__cta {
          font-size: .95rem; font-weight: 700; color: var(--teal);
          display: inline-flex; align-items: center; gap: 6px;
          transition: all .3s ease; margin-top: auto;
          width: fit-content;
        }
        .feat-card__cta-arrow { transition: transform .3s ease; }

        /* ── Journey Cards – Travel Journal style ── */
        .journey-card {
          display: flex; gap: 1.5rem; padding: 1.2rem 0;
          position: relative;
          animation: slideUp .7s cubic-bezier(.22,1,.36,1) both;
          align-items: flex-start;
        }

        /* Connecting route timeline */
        .journey-card__node {
          display: flex; flex-direction: column; align-items: center;
          width: 28px; flex-shrink: 0; padding-top: 8px;
        }
        .journey-card__node-dot {
          width: 14px; height: 14px; border-radius: 50%;
          background: linear-gradient(135deg, var(--teal), var(--teal-dark));
          border: 3px solid white;
          box-shadow: 0 0 0 2px var(--teal), 0 3px 10px rgba(33,165,160,0.2);
          position: relative; z-index: 2;
          transition: transform .3s ease, box-shadow .3s ease;
        }
        .journey-card:hover .journey-card__node-dot {
          transform: scale(1.25);
          box-shadow: 0 0 0 3px var(--teal), 0 4px 16px rgba(33,165,160,0.35);
        }
        .journey-card__node-line {
          width: 2px; flex: 1; min-height: 100%;
          background: linear-gradient(180deg, var(--teal), rgba(33,165,160,0.1));
          margin-top: 4px;
        }
        /* Hide line on last card */
        .journey-card:last-child .journey-card__node-line { display: none; }

        /* Polaroid-style image */
        .journey-card__polaroid {
          width: 200px; flex-shrink: 0; position: relative;
          padding: 6px 6px 30px 6px;
          background: white;
          border-radius: 4px;
          box-shadow: 0 4px 18px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.03);
          transform: rotate(var(--polaroid-rotate, 0deg));
          transition: transform .5s cubic-bezier(.22,1,.36,1), box-shadow .5s ease;
        }
        .journey-card:nth-child(odd) .journey-card__polaroid {
          --polaroid-rotate: -1.5deg;
        }
        .journey-card:nth-child(even) .journey-card__polaroid {
          --polaroid-rotate: 1.5deg;
        }
        .journey-card:hover .journey-card__polaroid {
          transform: rotate(0deg) scale(1.04);
          box-shadow: 0 8px 30px rgba(33,165,160,0.15), 0 1px 3px rgba(0,0,0,0.03);
        }
        .journey-card__photo {
          width: 100%; height: 150px; overflow: hidden;
          border-radius: 2px; position: relative;
        }
        .journey-card__photo img {
          width: 100%; height: 100%; object-fit: cover;
          transition: transform .6s ease;
        }
        .journey-card:hover .journey-card__photo img { transform: scale(1.1); }
        .journey-card__photo-badge {
          position: absolute; bottom: 6px; left: 6px;
          background: rgba(33,165,160,0.88);
          color: white; font-size: .8rem; font-weight: 700;
          padding: 2px 10px 2px 8px; border-radius: 100px;
          display: inline-flex; align-items: center; gap: 4px;
          backdrop-filter: blur(4px);
          text-transform: uppercase; letter-spacing: .04em;
        }
        /* Postmark stamp */
        .journey-card__stamp {
          position: absolute; top: -6px; right: -6px;
          opacity: 0; transform: translateY(-4px);
          transition: all .5s ease .1s;
        }
        .journey-card:hover .journey-card__stamp {
          opacity: 1; transform: translateY(0);
        }

        /* Content side */
        .journey-card__content {
          flex: 1; min-width: 0;
          display: flex; flex-direction: column;
          padding-top: 4px;
        }
        .journey-card__meta {
          display: flex; align-items: center; gap: 8px;
          margin-bottom: .6rem; flex-wrap: wrap;
        }
        .journey-card__meta-date, .journey-card__meta-read {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: .95rem; font-weight: 600; color: var(--grey);
        }
        .journey-card__title {
          font-family: 'Inter', sans-serif;
          font-weight: 800;
          font-size: 1.7rem;
          color: var(--black-dark); line-height: 1.35;
          margin: 0 0 .7rem; text-decoration: none;
          display: flex; align-items: flex-start; gap: 8px;
          transition: color .25s ease;
        }
        .journey-card__title:hover { color: var(--teal); }
        .journey-card__pin-icon {
          flex-shrink: 0; margin-top: 6px;
          color: var(--coral); transition: transform .3s ease;
        }
        .journey-card__title:hover .journey-card__pin-icon {
          transform: translateY(-2px);
        }
        .journey-card__excerpt {
          font-size: 1.2rem; color: var(--grey);
          line-height: 1.7; font-style: italic;
          margin: 0 0 1rem; padding-left: 0;
          display: -webkit-box; -webkit-line-clamp: 2;
          -webkit-box-orient: vertical; overflow: hidden;
        }
        .journey-card__cta {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 1.05rem; font-weight: 700; color: var(--teal);
          text-decoration: none; position: relative;
          transition: gap .3s ease; width: fit-content;
          margin-top: auto;
        }
        .journey-card__cta span { position: relative; z-index: 1; }
        .journey-card__cta:hover { gap: 12px; color: var(--coral); }
        .journey-card__cta-glide {
          position: absolute; bottom: 0; left: 0;
          height: 2px; width: 0;
          background: var(--coral);
          transition: width .3s ease;
        }
        .journey-card__cta:hover .journey-card__cta-glide { width: 100%; }

        /* Stagger */
        .journey-card:nth-child(1) { animation-delay: 0.05s; }
        .journey-card:nth-child(2) { animation-delay: 0.1s; }
        .journey-card:nth-child(3) { animation-delay: 0.15s; }
        .journey-card:nth-child(4) { animation-delay: 0.2s; }
        .journey-card:nth-child(5) { animation-delay: 0.25s; }
        .journey-card:nth-child(6) { animation-delay: 0.3s; }

        @media (max-width: 640px) {
          .journey-card { flex-direction: column !important; gap: 0.75rem; padding: 0.8rem 0; }
          .journey-card__node { display: none; }
          .journey-card__polaroid { width: 100%; }
          .journey-card__photo { height: 180px; }
          .journey-card:nth-child(odd) .journey-card__polaroid { --polaroid-rotate: 0deg; }
          .journey-card:nth-child(even) .journey-card__polaroid { --polaroid-rotate: 0deg; }
        }

        /* ── Sidebar ── */
        .sidebar-card { background: white; border-radius: 20px; border: 1px solid rgba(0,0,0,.06); padding: 1.8rem; margin-bottom: 1.8rem; }
        .sidebar-teal {
          background: linear-gradient(145deg, #0D4744 0%, #21A5A0 100%);
          border-radius: 20px; padding: 2rem; color: white; margin-bottom: 1.8rem;
          position: relative; overflow: hidden;
        }
        .sidebar-teal::before {
          content: ''; position: absolute; right: -30px; bottom: -30px;
          width: 160px; height: 160px; border-radius: 50%;
          background: rgba(255,255,255,.06);
        }
        .sidebar-teal::after {
          content: ''; position: absolute; left: -20px; top: -20px;
          width: 100px; height: 100px; border-radius: 50%;
          background: rgba(255,255,255,.04);
        }

        /* ── Pagination ── */
        .page-btn {
          padding: 10px 22px; border-radius: 12px; border: 1.5px solid #e0e0e0;
          background: white; font-size: .82rem; font-weight: 600; color: var(--grey);
          cursor: pointer; transition: all .3s ease; outline: none;
        }
        .page-btn:not(:disabled):hover { border-color: var(--teal); color: var(--teal); background: var(--teal-light); transform: translateY(-1px); }
        .page-btn:disabled { opacity: .35; cursor: not-allowed; }

        /* ── Search ── */
        .search-bar {
          display: flex; align-items: center; background: rgba(255,255,255,0.12);
          backdrop-filter: blur(12px); border: 1px solid rgba(255,255,255,0.15);
          border-radius: 100px; padding: 10px 20px; gap: 10px;
          width: 100%; max-width: 440px; transition: all .3s ease;
        }
        .search-bar:focus-within { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.3); box-shadow: 0 0 30px rgba(33,165,160,0.15); }
        .search-bar input { background: transparent; border: none; outline: none; width: 100%; font-size: 1rem; color: white; }
        .search-bar input::placeholder { color: rgba(255,255,255,0.7); }
        .clear-btn { background: none; border: none; cursor: pointer; color: rgba(255,255,255,0.5); padding: 0; line-height: 0; }
        .clear-btn:hover { color: var(--coral); }

        /* ── Newsletter ── */
        .newsletter-input { width: 100%; padding: 12px 16px; border-radius: 12px; border: 1.5px solid #e0e0e0; font-size: .875rem; outline: none; transition: border-color .25s; }
        .newsletter-input:focus { border-color: var(--teal); box-shadow: 0 0 0 3px var(--teal-glow); }
        .newsletter-btn {
          width: 100%; padding: 12px; border-radius: 12px;
          background: linear-gradient(135deg, var(--coral), var(--coral-dark));
          color: white; font-weight: 700; font-size: .875rem; border: none;
          cursor: pointer; transition: all .3s ease;
        }
        .newsletter-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(224,90,73,0.3); }

        /* ── Quick tag chips ── */
        .tag-chip {
          padding: 6px 14px; border-radius: 100px; border: 1.5px solid #e0e0e0;
          background: white; font-size: .75rem; font-weight: 600; color: var(--grey);
          cursor: pointer; transition: all .3s ease;
        }
        .tag-chip:hover { border-color: var(--teal); color: var(--teal); background: var(--teal-light); transform: translateY(-1px); }

        /* ── Section dividers ── */
        .section-divider {
          display: flex; align-items: center; gap: 12px; margin-bottom: 1.5rem;
        }
        .section-divider__line {
          flex: 1; height: 1px;
          background: linear-gradient(90deg, var(--teal), transparent);
        }

        /* ── Empty state ── */
        .empty-state { background: white; border-radius: 20px; padding: 3rem; text-align: center; border: 1px solid rgba(0,0,0,.05); }

        /* ── Scroll-reveal grid ── */
        .reveal-grid .feat-card:nth-child(1) { animation-delay: .05s; }
        .reveal-grid .feat-card:nth-child(2) { animation-delay: .15s; }
        .reveal-grid .feat-card:nth-child(3) { animation-delay: .25s; }
        .reveal-grid .feat-card { animation: slideUp .7s cubic-bezier(.22,1,.36,1) both; }

        /* ── Travel stats bar ── */
        .stats-bar {
          background: linear-gradient(135deg, #0a2a28 0%, #1a5a56 50%, #0a2a28 100%);
          border-radius: 18px; padding: 2rem 2.5rem;
          display: flex; justify-content: space-around; flex-wrap: wrap; gap: 1.5rem;
          position: relative; overflow: hidden;
        }
        .stats-bar::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(ellipse at 30% 50%, rgba(33,165,160,0.12), transparent 60%);
        }
        .stat-item { text-align: center; position: relative; z-index: 1; }

        @media (max-width: 640px) {
          .stats-bar { padding: 1.5rem; flex-direction: row; gap: 1rem; }
          .stat-item { flex: 1 0 45%; }
        }
      `}</style>

      {/* ════════════════════════════════════════════════ */}
      {/* HERO SECTION – Travel Explorer                  */}
      {/* ════════════════════════════════════════════════ */}
      <header
        ref={heroRef}
        className="relative overflow-hidden"
        style={{
          minHeight: 520,
          maxHeight: 620,
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Crossfading background images */}
        {HERO_IMAGES.map((img, i) => (
          <div
            key={i}
            className="hero-crossfade"
            style={{
              opacity: i === heroImgIndex ? 1 : 0,
              backgroundImage: `url(${img})`,
              transform: `translateY(${parallaxOffset}px) scale(${i === heroImgIndex ? 1.05 : 1})`,
              transition: i === heroImgIndex
                ? "opacity 1.4s ease-in-out"
                : "opacity 1.4s ease-in-out, transform 0.1s linear",
            }}
          />
        ))}

        {/* Gradient overlay - travel inspired */}
        <div style={{
          position: "absolute", inset: 0,
          background: `
            linear-gradient(165deg,
              rgba(10,50,46,.92) 0%,
              rgba(10,42,40,.75) 30%,
              rgba(15,15,15,.55) 55%,
              rgba(180,50,40,.40) 100%
            )
          `,
        }} />

        {/* Floating pin */}
        <div className="float-pin" style={{
          position: "absolute", left: "8%", bottom: "25%",
          opacity: .15, pointerEvents: "none",
        }}>
          <svg width="32" height="44" viewBox="0 0 24 34" fill="white">
            <path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 22 12 22s12-13 12-22C24 5.37 18.63 0 12 0zm0 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
          </svg>
        </div>

        {/* Animated route path at bottom */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, width: "100%", height: 80,
          opacity: .18, pointerEvents: "none",
        }}>
          <svg viewBox="0 0 800 80" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
            <path d="M0,60 Q100,20 200,50 T400,30 T600,55 T800,40"
              fill="none" stroke="white" strokeWidth="2" strokeDasharray="8,10"
              style={{ strokeDashoffset: 500, animation: "routeDash 4s ease 1s forwards" }} />
            <path d="M0,65 Q150,40 300,60 T550,40 T800,55"
              fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1" strokeDasharray="5,8"
              style={{ strokeDashoffset: 600, animation: "routeDash 5s ease 1.5s forwards" }} />
          </svg>
        </div>

        {/* Hero Content */}
        <div style={{
          position: "relative", zIndex: 2, width: "100%",
          maxWidth: 1200, margin: "0 auto",
          padding: "5rem 1.5rem 4rem",
          textAlign: "center", color: "white",
        }}>
          <div className="anim-hero-1" style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            border: "1px solid rgba(255,255,255,.2)",
            borderRadius: 100, padding: "5px 20px",
            fontSize: ".7rem", fontWeight: 700, letterSpacing: ".25em",
            textTransform: "uppercase", color: "rgba(255,255,255,.7)",
            marginBottom: "1.2rem",
          }}>
            <Icon path={mdiCompass} size={0.65} />
            Stories & Guides
          </div>

          <h1 className="anim-hero-2" style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "clamp(3.2rem, 8vw, 5.5rem)",
            fontWeight: 800, lineHeight: 1.1, letterSpacing: "-.02em",
            margin: "0 0 1.2rem",
          }}>
            Mobility Smarter.{" "}
            <span style={{ fontStyle: "italic", color: "#4de8e0" }}>Travel Better.</span>
          </h1>

          <p className="anim-hero-3" style={{
            fontSize: "clamp(1.1rem, 2.5vw, 1.4rem)",
            color: "rgba(255,255,255,.85)",
            maxWidth: 600, margin: "0 auto 2.5rem", lineHeight: 1.7,
          }}>
            Expert travel guides, safety tips, corporate mobility strategies — 
            your companion for life on the move.
          </p>

          <div className="anim-hero-4" style={{
            display: "flex", flexWrap: "wrap", alignItems: "center",
            justifyContent: "center", gap: "1rem",
          }}>
            <div className="search-bar">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search destinations, tips, guides..."
                style={{ fontSize: "1rem" }}
              />
              {query && (
                <button className="clear-btn" onClick={clearSearch}>
                  <Icon path={mdiClose} size={0.7} />
                </button>
              )}
            </div>
            <button
              onClick={() => {
                if (!query.trim()) {
                  const searchInput = document.querySelector('.search-bar input');
                  if (searchInput) searchInput.focus();
                }
              }}
              style={{
                background: "linear-gradient(135deg, var(--coral), var(--coral-dark))",
                color: "white", fontWeight: 800,
                padding: "14px 36px", borderRadius: 100, fontSize: "1rem",
                border: "none", cursor: "pointer",
                transition: "all .3s ease",
                whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 8,
                letterSpacing: "0.02em",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(224,90,73,0.35)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
            >
              Search
              <Icon path={mdiChevronDoubleRight} size={0.7} />
            </button>
          </div>
        </div>

        {/* Image indicator dots */}
        <div style={{
          position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)",
          display: "flex", gap: 8, zIndex: 5,
        }}>
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setHeroImgIndex(i)}
              style={{
                width: i === heroImgIndex ? 24 : 8, height: 8,
                borderRadius: 4, border: "none",
                background: i === heroImgIndex ? "white" : "rgba(255,255,255,0.35)",
                cursor: "pointer", transition: "all .4s ease",
              }}
            />
          ))}
        </div>
      </header>

      {/* ════════════════════════════════════════════════ */}
      {/* MAIN CONTENT                                     */}
      {/* ════════════════════════════════════════════════ */}
      <main style={{
        maxWidth: 1200, margin: "0 auto",
        padding: "0 1.5rem 5rem",
        marginTop: "-3rem", position: "relative", zIndex: 10,
      }}>

        {/* ── Category Tabs ── */}
        <CategoryTabsGem
          categories={categories}
          activeCategory={activeCategory}
          onChange={(val) => setActiveCategory(val)}
        />

        {/* ── Loading / Empty / Content ── */}
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "4rem 0" }}>
            <Loading />
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: "3.5rem", marginBottom: ".5rem" }}>🗺️</div>
            <p style={{ fontWeight: 700, fontSize: "1.1rem", color: "var(--black)" }}>No blogs found</p>
            <p style={{ color: "var(--grey)", fontSize: ".9rem", marginTop: ".4rem" }}>
              Try a different category or search keyword.
            </p>
            <button onClick={() => { setQuery(""); setActiveCategory("All Topics"); }}
              style={{
                marginTop: "1.2rem", padding: "10px 28px", borderRadius: 100,
                border: "1.5px solid var(--teal)", color: "var(--teal)",
                background: "white", fontWeight: 600, cursor: "pointer",
                transition: "all .3s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--teal)"; e.currentTarget.style.color = "white"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "white"; e.currentTarget.style.color = "var(--teal)"; }}>
              Show All Articles
            </button>
          </div>
        ) : (
          <>
            {/* ── Trending Featured Section ── */}
            <section ref={featuredRef} style={{ marginBottom: "2rem" }}>
              <div className="section-divider">
                <span style={{
                  width: 5, height: 30, background: "var(--coral)",
                  borderRadius: 4, display: "inline-block", flexShrink: 0,
                }} />
                <h2 style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "1.6rem", fontWeight: 800, color: "var(--black-dark)",
                  margin: 0, whiteSpace: "nowrap",
                }}>
                  Trending Guides
                </h2>
                <div className="section-divider__line" />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))",
                  gap: "1.5rem",
                }}
              >
                {featuredBlogs.map((b) => {
                  const key = guessCategoryKeyFromBlog(b);
                  const tag = categoryBadgeLabelFromKey(key).toUpperCase();
                  return <FeaturedBlogCardGem key={b?.id || b?.slug || b?.title} blog={b} tagLabel={tag} />;
                })}
              </div>
            </section>

            {/* ── Stats Bar ── */}
            <div ref={statsRef} style={{
              marginBottom: "1rem",
              opacity: statsVisible ? 1 : 0,
              transform: statsVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all .6s ease .1s",
            }}>
              <div className="stats-bar">
                {TRAVEL_STATS.map((stat, i) => (
                  <div key={stat.label} className={`stat-item ${statsVisible ? "stat-item-enter" : ""}`}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 12,
                      background: "rgba(255,255,255,0.08)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      margin: "0 auto .4rem",
                    }}>
                      <Icon path={stat.icon} size={0.9} color="rgba(255,255,255,0.7)" />
                    </div>
                    <div style={{
                      fontSize: "1.4rem", fontWeight: 800, color: "white",
                      lineHeight: 1.2,
                    }}>{stat.value}</div>
                    <div style={{
                      fontSize: ".75rem", color: "rgba(255,255,255,0.6)",
                      fontWeight: 500, letterSpacing: ".05em",
                    }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Blog Feed ── */}
            <div ref={articlesRef} style={{ maxWidth: 960, margin: "0 auto" }}>
                <div className="section-divider" style={{ marginBottom: "0.8rem" }}>
                  <span style={{
                    width: 5, height: 26, background: "var(--teal)",
                    borderRadius: 4, display: "inline-block", flexShrink: 0,
                  }} />
                  <h2 style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "1.8rem", fontWeight: 800, color: "var(--black-dark)",
                    margin: 0, whiteSpace: "nowrap",
                  }}>
                    Latest Articles
                  </h2>
                  <div className="section-divider__line" />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {pagedBlogs.map((b, idx) => {
                    const key = guessCategoryKeyFromBlog(b);
                    const badgeLabel = categoryBadgeLabelFromKey(key);
                    const badgeIcon =
                      key === "Safety & Tips" ? mdiShieldCheckOutline
                      : key === "Airport Transfer Tips" ? mdiAirplaneTakeoff
                      : key === "Corporate Travel" ? mdiBriefcaseOutline
                      : key === "Road Trip Advice" ? mdiMapMarkerPath
                      : mdiCellphone;
                    return (
                      <BlogListItemGem
                        key={b?.id || b?.slug || b?.title || idx}
                        blog={b}
                        badgeLabel={badgeLabel}
                        badgeIconPath={badgeIcon}
                        delay={`${Math.min(idx * 80, 240)}ms`}
                        index={idx}
                      />
                    );
                  })}
                </div>

                {/* ── Sidebar removed ── */}

                {/* Pagination */}
                <div style={{
                  display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 12,
                  paddingTop: "2.5rem",
                }}>
                  <button
                    className="page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    ← Prev
                  </button>
                  <span style={{ fontSize: ".82rem", color: "var(--grey)", fontWeight: 600 }}>
                    Page {page} / {totalPages}
                  </span>
                  <button
                    className="page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next →
                  </button>
                </div>
              </div>
          </>
        )}
      </main>
    </div>
  );
}